///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, concatMap, finalize, map, tap } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { UserService } from '../../service/user.service';
import {
    closeUserDialog,
    createUser,
    deleteSale,
    deleteUser,
    engineerCreationHandling,
    engineerDeletionHandling,
    engineerModificationHandling,
    FailureResponse,
    getCurrentAccount,
    getShowEditButton,
    getSuccessorCompaniesList,
    getUserActivityEventsPage,
    getUserActivityEventTypes,
    getUsersPage,
    loadPage,
    modifyUser,
    setCurrentAccount,
    setShowEditButton,
    setSuccessorCompaniesList,
    setUserActivityEventsPage,
    setUserActivityEventTypes,
    setUsersPage,
    userCreationHandling,
    userDeletionHandling,
    userModificationHandling,
    usersActivitySearch
} from '../users-list.actions';
import { EMPTY, from, Observable, of, switchMap } from 'rxjs';
import { Principal } from '../../../shared/auth/principal.service';
import { UserAuthorities } from '../../../services/user/user-authorities';
import { ITEMS_PER_PAGE } from '../../../shared/constants/pagination.constants';
import { ActivatedRoute, Router } from '@angular/router';
import { CUSTOMERS_BASE_URL } from '../../../customers-config/customers.route';
import { PagedModel } from '../../../generated/spring-types.model';
import { UserActivityEventDTO } from '../../../generated/shared-types.model';
import { selectCurrentRoute } from '../../../router-store/router.selector';
import { ACCOUNTANTS_BASE_URL, CUSTOMERS_SUPPORT_BASE_URL, SALES_BASE_URL } from '../../../engineers-config/engineers.constants';
import { USERS_BASE_URL } from '../../../users-config/users.route';
import { SuiUserDTO } from '../../../generated/shared-sui-types.model';
import { selectCurrentCustomer } from '../../../shared/store/customerAccordionStore/accordion.selectors';
import { LoaderIds, PreloaderService } from '../../../services/preloader.service';
import { GtthUserActivityService } from '../../../system-reports/user-activity-overview/user-activity.service';
import { USER_ACTIVITY_OVERVIEW_BASE_URL } from '../../../system-reports/system-reports.constants';
import { concatLatestFrom } from '@ngrx/operators';
import { Pageable } from '../../../services/pageable.model';

@Injectable()
export class UsersListEffects {
    loadPage$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(loadPage),
            switchMap((action) => {
                return from(
                    this.router.navigate([], {
                        queryParams: { page: action.page },
                        queryParamsHandling: 'merge'
                    })
                ).pipe(map(() => getUsersPage()));
            })
        );
    });

    getUsersPage$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(getUsersPage),
            tap(() => this.preloader.show()),
            concatLatestFrom(() => [this.store.select(selectCurrentRoute), this.store.select(selectCurrentCustomer)]),
            switchMap(([, router, currentCustomer]) => {
                const currentTabName = router.data.tabName;
                const customerFilter = router.queryParams.filter ? router.queryParams.filter : undefined;
                const customerId = router.queryParams.company_id ? router.queryParams.company_id : undefined;
                const requestObject = {
                    page: router.queryParams.page ? +router.queryParams.page : 1,
                    size: ITEMS_PER_PAGE,
                    sort: ['id', 'desc']
                } as Pageable;

                let request$: Observable<PagedModel<SuiUserDTO>>;

                switch (currentTabName) {
                    case ACCOUNTANTS_BASE_URL:
                        request$ = this.userService.loadAllAccountants(requestObject);
                        break;
                    case CUSTOMERS_SUPPORT_BASE_URL:
                        request$ = this.userService.loadAllCustomersSupport(requestObject);
                        break;
                    case SALES_BASE_URL:
                        request$ = this.userService.loadAllSales(requestObject);
                        break;
                    case USERS_BASE_URL:
                        request$ = this.userService.loadUsersForCurrentUserCustomer(requestObject);
                        break;
                    case CUSTOMERS_BASE_URL:
                        if (customerId) {
                            request$ = this.userService.findAllByCustomerId(customerId, requestObject, customerFilter);
                        } else {
                            request$ = of({ content: [], page: { totalElements: 0 } } as PagedModel<SuiUserDTO>);
                        }
                        break;
                    case USER_ACTIVITY_OVERVIEW_BASE_URL:
                        if (customerId) {
                            request$ = this.userService.findAllByCustomerId(customerId, {
                                size: currentCustomer.numberOfUsers
                            } as Pageable);
                        } else {
                            request$ = of({ content: [], page: { totalElements: 0 } } as PagedModel<SuiUserDTO>);
                        }
                        break;
                    default:
                        request$ = of({ content: [], page: { totalElements: 0 } } as PagedModel<SuiUserDTO>);
                }

                return request$.pipe(
                    finalize(() => this.preloader.hide()),
                    tap((userPage: PagedModel<SuiUserDTO>) => {
                        let page = requestObject.page <= 0 ? 1 : requestObject.page;
                        page = page >= userPage.page.totalPages ? userPage.page.totalPages : page;
                        this.router.navigate([], {
                            queryParams: { page: page },
                            queryParamsHandling: 'merge'
                        });
                    })
                );
            }),
            map((usersPage: PagedModel<SuiUserDTO>) => setUsersPage({ usersPage }))
        );
    });

    getUserActivityEventsPage$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(getUserActivityEventsPage),
            tap(() => this.preloader.show()),
            switchMap((action) => {
                const queryParams = action.selectedUserQueryParams;
                if (queryParams) {
                    return this.userActivityService.getEvents(queryParams).pipe(finalize(() => this.preloader.hide()));
                } else {
                    return of({ content: [], page: { totalElements: 0 } } as PagedModel<UserActivityEventDTO>);
                }
            }),
            map((userEventsPage) => setUserActivityEventsPage({ userEventsPage }))
        );
    });

    getUserActivityEventTypes$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(getUserActivityEventTypes),
            switchMap(() => {
                return this.userActivityService.getEventTypes();
            }),
            map((userEventTypes) => setUserActivityEventTypes({ userEventTypes }))
        );
    });

    usersActivitySearch$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(usersActivitySearch),
                tap((action) =>
                    this.router.navigate([], {
                        queryParams: action.queryParams,
                        relativeTo: this.activatedRoute
                    })
                )
            ),
        { dispatch: false }
    );

    getShowEditButton$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(getShowEditButton),
            concatLatestFrom(() => [this.store.select(selectCurrentRoute)]),
            switchMap(([, router]) => {
                const currentTabName = router.data.tabName;

                switch (currentTabName) {
                    case ACCOUNTANTS_BASE_URL:
                        return from(this.principal.hasAnyAuthority([UserAuthorities.SIKLU_ADMIN, UserAuthorities.SIKLU_ACCOUNTANT]));
                    case CUSTOMERS_SUPPORT_BASE_URL:
                        return from(
                            this.principal.hasAnyAuthority([
                                UserAuthorities.SIKLU_ADMIN,
                                UserAuthorities.SIKLU_ACCOUNTANT,
                                UserAuthorities.SIKLU_CUSTOMER_SUPPORT
                            ])
                        );
                    case SALES_BASE_URL:
                        return from(this.principal.hasAnyAuthority(UserAuthorities.ALL_SIKLU));
                    case USERS_BASE_URL:
                        return from(this.principal.hasAnyAuthority([UserAuthorities.COMPANY_ADMIN]));
                    case CUSTOMERS_BASE_URL:
                        return from(this.principal.hasAnyAuthority(UserAuthorities.ALL_SIKLU));
                }
            }),
            map((showEditButton) => {
                return setShowEditButton({ showEditButton });
            })
        );
    });

    getCurrentAccount$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(getCurrentAccount),
            switchMap(() => from(this.principal.identity())),
            map((currentAccount) => setCurrentAccount({ currentAccount: new SuiUserDTO(currentAccount) }))
        );
    });

    getSuccessorCompaniesList$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(getSuccessorCompaniesList),
            tap(() => this.preloader.show(LoaderIds.SALE_DELETE_DIALOG)),
            switchMap((action) =>
                this.userService
                    .getSuccessorCompaniesList(action.saleId)
                    .pipe(finalize(() => this.preloader.hide(LoaderIds.SALE_DELETE_DIALOG)))
            ),
            map((successorCompaniesList) => setSuccessorCompaniesList({ successorCompaniesList: successorCompaniesList }))
        );
    });

    createUser$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(createUser),
            tap(() => this.preloader.show(LoaderIds.USER_DIALOG_PRELOADER_ID)),
            switchMap((action) =>
                this.userService.create(action.newUser).pipe(
                    finalize(() => this.preloader.hide(LoaderIds.USER_DIALOG_PRELOADER_ID)),
                    map(() => action),
                    catchError(() => {
                        this.store.dispatch(FailureResponse());
                        return EMPTY;
                    })
                )
            ),
            concatMap((action) => {
                if (!action) {
                    return EMPTY;
                }

                const actions = [];
                if (UserAuthorities.ALL_COMPANY.includes(action.newUser.authorities[0].value)) {
                    actions.push(userCreationHandling());
                } else {
                    actions.push(engineerCreationHandling());
                }
                actions.push(closeUserDialog());
                return actions;
            })
        );
    });

    modifyUser$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(modifyUser),
            tap(() => this.preloader.show(LoaderIds.USER_DIALOG_PRELOADER_ID)),
            switchMap((action) =>
                this.userService.update(action.updatedUser).pipe(
                    finalize(() => this.preloader.hide(LoaderIds.USER_DIALOG_PRELOADER_ID)),
                    map(() => action),
                    catchError(() => {
                        this.store.dispatch(FailureResponse());
                        return EMPTY;
                    })
                )
            ),
            concatMap((action) => {
                if (!action) {
                    return EMPTY;
                }

                const actions = [];
                if (UserAuthorities.ALL_COMPANY.includes(action.updatedUser.authorities[0].value)) {
                    actions.push(userModificationHandling());
                } else {
                    actions.push(engineerModificationHandling());
                }
                actions.push(closeUserDialog());
                return actions;
            })
        );
    });

    deleteUser$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(deleteUser),
            switchMap((action) =>
                this.userService.delete(action.id).pipe(
                    map(() => action),
                    catchError(() => {
                        this.store.dispatch(FailureResponse());
                        return EMPTY;
                    })
                )
            ),
            concatMap((action) => {
                if (!action) {
                    return EMPTY;
                }

                const actions = [];
                if (UserAuthorities.ALL_COMPANY.includes(action.deletedUser.authorities[0].value)) {
                    actions.push(userDeletionHandling());
                } else {
                    actions.push(engineerDeletionHandling());
                }
                actions.push(closeUserDialog());
                return actions;
            })
        );
    });

    deleteSale$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(deleteSale),
            switchMap((action) => {
                if (action.reassignSaleId !== undefined) {
                    return this.userService.deleteSale(action.id, action.reassignSaleId).pipe(
                        map(() => action),
                        catchError(() => {
                            this.store.dispatch(FailureResponse());
                            return EMPTY;
                        })
                    );
                } else {
                    return this.userService.delete(action.id).pipe(
                        map(() => action),
                        catchError(() => {
                            this.store.dispatch(FailureResponse());
                            return EMPTY;
                        })
                    );
                }
            }),
            concatMap((action) => {
                if (!action) {
                    return EMPTY;
                }
                return [engineerDeletionHandling(), closeUserDialog()];
            })
        );
    });

    constructor(
        private readonly store: Store,
        private readonly actions$: Actions,
        private readonly router: Router,
        private readonly activatedRoute: ActivatedRoute,
        private readonly userService: UserService,
        private readonly userActivityService: GtthUserActivityService,
        private readonly principal: Principal,
        private readonly preloader: PreloaderService
    ) {}
}
